// The live kitchen display's server state (spec/13 §6.2/§6.3, F-FNB-04/-05/-06), as TanStack Query
// hooks over the generated client — `packages/contracts/src/routes/kitchen.ts`:
//
//   GET  …/venues/{v}/kitchen/tickets                 the tickets by preparation station   kitchen:read
//   POST …/venues/{v}/kitchen/tickets/{id}/state      advance one, or recall it            kitchen:update
//   POST …/venues/{v}/kitchen/tickets/{id}/print      render one for its printer           kitchen:read
//   GET  …/venues/{v}/food-orders/{id}                the order a ticket belongs to         orders:read
//   POST …/venues/{v}/food-orders/{id}/cancel         cancel it, and its tickets           orders:void
//
// **Liveness.** The display is driven by the `kitchen.ticket.*` push on `venue:{v}:kitchen`
// (spec/09 §6.3), the message the food-and-kitchen foundation deferred, wired server-side and now
// consumed by the console's own WebSocket client (`../../realtime`, `kitchen/realtime.ts`): while the
// socket is live it applies each frame's whole ticket to this cache and the interval below is switched
// off. When the socket is not live — reduced motion, or disconnected — the interval is the fallback,
// with the REST snapshot as the recovery mechanism (spec/09 §4).

import type { AdvanceKitchenTicketRequest, FoodOrder, KitchenTicket, KitchenTicketPrintJob, KitchenTicketState } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { useRealtimeLive } from '../../realtime';

/** How often the board is refetched while it is open and the live push is not carrying it. */
const KITCHEN_REFRESH_MS = 8_000;

export const kitchenKeys = {
  tickets: (organizationId: string, venueId: string, state: string) =>
    ['kitchen', organizationId, venueId, 'tickets', state] as const,
  order: (organizationId: string, venueId: string, orderId: string) => ['kitchen', organizationId, venueId, 'order', orderId] as const,
};

/**
 * A venue's kitchen tickets. With no `state` the server returns the active board (new, preparing,
 * ready); pass `delivered` for the recall list (spec/13 §6.2).
 */
export function useKitchenTickets(organizationId: string, venueId: string, state?: KitchenTicketState, enabled = true) {
  const api = useApi();
  const live = useRealtimeLive();
  return useQuery<readonly KitchenTicket[]>({
    queryKey: kitchenKeys.tickets(organizationId, venueId, state ?? 'active'),
    enabled,
    refetchInterval: live ? false : KITCHEN_REFRESH_MS,
    refetchOnWindowFocus: !live,
    queryFn: async () =>
      (
        await api.listKitchenTickets({
          path: { organizationId, venueId },
          ...(state === undefined ? {} : { query: { state } }),
        })
      ).tickets,
  });
}

/** After a ticket changes, re-read both boards so a moved card leaves one column and joins the next. */
function useKitchenInvalidator(organizationId: string, venueId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: kitchenKeys.tickets(organizationId, venueId, 'active') });
    void queryClient.invalidateQueries({ queryKey: kitchenKeys.tickets(organizationId, venueId, 'delivered') });
  };
}

/** Advance a ticket to the next state, or recall it one step back (F-FNB-04). */
export function useAdvanceKitchenTicket(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useKitchenInvalidator(organizationId, venueId);
  return useMutation({
    mutationFn: ({ ticketId, state }: { ticketId: string; state: AdvanceKitchenTicketRequest['state'] }) =>
      api.advanceKitchenTicket({ path: { organizationId, venueId, ticketId }, body: { state } }),
    onSuccess: () => invalidate(),
  });
}

/** Render a ticket for its preparation station's printer, and say where it routed (F-FNB-05). */
export function usePrintKitchenTicket(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation<KitchenTicketPrintJob, unknown, { ticketId: string }>({
    mutationFn: ({ ticketId }) => api.printKitchenTicket({ path: { organizationId, venueId, ticketId } }),
  });
}

/** The food order a ticket belongs to, with every line and its totals (F-POS-15). */
export function useFoodOrder(organizationId: string, venueId: string, orderId: string) {
  const api = useApi();
  return useQuery<FoodOrder>({
    queryKey: kitchenKeys.order(organizationId, venueId, orderId),
    queryFn: () => api.getFoodOrder({ path: { organizationId, venueId, orderId } }),
  });
}

/** Cancel an unsettled food order; the server cancels its kitchen tickets with it (F-POS-15). */
export function useCancelFoodOrder(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useKitchenInvalidator(organizationId, venueId);
  return useMutation({
    mutationFn: ({ orderId }: { readonly orderId: string }) => api.cancelFoodOrder({ path: { organizationId, venueId, orderId } }),
    onSuccess: (order) => {
      queryClient.setQueryData(kitchenKeys.order(organizationId, venueId, order.id), order);
      invalidate();
    },
  });
}
